<template>
  <div class="table-wrapper Table">
    <span class="number-of-items" style="display: none">{{ numberOfItems }}</span>

    <table
      class="table"
      id="items-table"
      
      :class="{
        firstColumnPadding,
        overflowingTableSpace,
      }"

      ref="table"
    >
      <Thead
        :value="computedColumnSizes"
        @input="size => columnSizes = size"
      
        :width="disableLoadingContent ? '100%' : width"
        :columns="columns"
        :columnVariables="columnVariables"
        :sort="sort"
        :disableLoadingContent="disableLoadingContent"
        :dragDisabledOnColumns="dragDisabledOnColumns"
        :order="order"
        :enableSorting="enableSorting"

        @update-column-order="v => $emit('update-column-order', v)"
        @set-order="v => $emit('set-order', v)"
        @set-sort="v => $emit('set-sort', v)"

        ref="thead"
      >
        <template #table-header="props">
          <slot name="table-header" v-bind="props"></slot>
        </template>
      
        <template v-for="(c, i) in columns" v-slot:[`header-${columnVariables[i]}`]>
          <slot
            :name="`header-${columnVariables[i]}`"
          ></slot>
        </template>
      </Thead>
      <tbody v-if="data && (data.length || isLoading)"
        :class="{hasHeightLimit: heightLimit, 'disable-ske': disableLoadingContent}"
        :style="{width: disableLoadingContent ? '100%' : width, height: heightLimit || 'auto'}"

        ref="tbody"
      >
      
        <TableRow v-for="(item, index) in computedData"
          :key="item && ((item.uid || item.sku || item) + index)"

          :removeCursor="removeCursor"
          :position="index"
          :selected="selected.includes(item && (item.uid || item.sku))"

          :id="item && (item.uid || item.sku)"

          @click.native="$emit('row-click', item)"
        >
          <slot name="column-select" :is-loading="isLoading" :selected="selected.includes(item && (item.uid || item.sku))" :row-index="index"></slot>
          <slot name="before-row" :is-loading="isLoading" :item="item" :row-index="index"></slot>

          <slot v-for="(cell, i) in columnVariables"
            :name="cell"
            :bindClass="[{capitalize: capitalize ? capitalize[i] : true}, skeletonClass]"
            :row-index="index"
            :is-loading="isLoading"
  
            :value="itemValue(item, cell)"
            :item="item"

            :styles="computedColumnSizes && computedColumnSizes[cell] && {
              'min-width': computedColumnSizes[cell] + 'px !important',
              'max-width': computedColumnSizes[cell] + 'px !important',
            }"
          >
            <TableCell :key="item && ((item.uid || item.sku || item) + i)"
              :uid="item && ((item.uid || item.sku || item) + i)"
              :capitalize="capitalize ? capitalize[i] : true"
              :skeletonClass="skeletonClass"

              :style="computedColumnSizes && computedColumnSizes[cell] && {
                'min-width': computedColumnSizes[cell] + 'px !important',
                'max-width': computedColumnSizes[cell] + 'px !important',
              }"
            >
              {{ itemValue(item, cell, true) }}

              <template #sub-cell>
                <slot :name="`after-${item && item.uid}-item`" :row-index="index"></slot>
              </template>
            </TableCell>
          </slot>


          <td class="d-flex align-items-center final-item d-flex" @click.stop>
            <div class="final-item-wrapper">
              <span>
                <slot name="final-column-item" :is-loading="isLoading" :item="item" :row-index="index"></slot>
              </span>
            </div>
          </td>
        </TableRow>
      </tbody>
      <div v-else class="d-flex justify-content-center align-items-center empty-state-wrapper">
        <slot name="empty-state" :is-loading="isLoading"></slot>
      </div>
    </table>
  </div>
</template>

<script>

import Thead from "./Thead.vue"

import TableCell from "./TableCell.vue"
import TableRow from "./TableRow.vue"

const SIZES_STORAGE_KEY = "sizes_storage_key:"

export default {
  name: 'l-table',
  components: {
    Thead,
    TableRow,
    TableCell,
  },
  props: {
    data: {
      type: [Array, String],
      required: false,
    },
    scrollingLimit: {
      type: Number,
      default: 8,
    },
    selected: {
      type: Array,
      default: () => ([]),
    },
    fieldsWithCommas: {
      type: Array,
      default: () => ([]),
      required: false,
    },

    heightLimit: String,
    sort: String,
    order: String,
    enableSorting: Boolean,
    disableLoadingContent: Boolean,
    dragDisabledOnColumns: Array,
    removeCursor: Boolean,
    firstColumnPadding: Boolean,
    skeletonClass: String,
    columnConfigurationId: String,
    columns: Array,
    columnVariables: Array,
    capitalize: Array,
    removeSkeletonArray: Boolean,
  },
  data() {
    return {
      totalCalculatedFirstRowWidth: null,
      theadWidth: null,
      viewportWidth: window.innerWidth,
      autoRemoveManualResizeField: null,
      
      columnSizes: {},
    }
  },
  mounted() {
    this.getViewportSize()
    this.interval = setInterval(() => {
      this.getViewportSize()
      this.resetDimensionsAndReCalculateWidth()
    }, 300)
    
    window.addEventListener("resize", this.getViewportSize)

    this.calculateWidthFromTheadElements(true)
  },
  beforeDestroy() {
    window.addEventListener("resize", this.getViewportSize)
    this.interval && clearInterval(this.interval)
  },
  methods: {
    calculateWidthFromTheadElements(overwriteByLocalStorage) {
      this.$nextTick(() => {
        const sizes = {...(this.columnSizes || {})}
        
        let addedNewSize
        ;([...document.querySelector("thead.TableThead tr").children]).forEach(el => {
          if (!sizes[el.dataset.columnvar]) {
            addedNewSize = true
            sizes[el.dataset.columnvar] = el.getBoundingClientRect().width
          }
        })
  
        if (addedNewSize) {
          this.columnSizes = sizes
        }

        if (overwriteByLocalStorage && this.columnConfigurationId) {
          let sizes = localStorage.getItem(this.sizesStorageKey)
          if (!sizes) return;

          try {
            this.columnSizes = JSON.parse(sizes)
          } catch (err) {}
        }
      })
    },
    
    getViewportSize() {
      this.viewportWidth = window.innerWidth
      if (this.$refs.thead) {
        this.theadWidth = this.$refs.thead.$el.clientWidth
      }
    },
    
    itemValue(item, column, addReplacement) {
      if (!item || !item[column]) {
        return addReplacement ? "---" : undefined
      }

      let str = item[column]

      if (this.fieldsWithCommas.includes(column) && !isNaN(parseFloat(str))) {
        return (parseFloat(str).toFixed(2) + "").replace(/\./, ",")
      }
      if (typeof str !== "string") return str
      return str === str.toUpperCase() ?
        (str[0].toUpperCase() + str.toLowerCase().slice(1)) :
        str
    },
    resetDimensionsAndReCalculateWidth() {
      this.totalCalculatedFirstRowWidth = null
      this.$nextTick(() => this.calculateTotalWidthOfFirstRow())
    },
    calculateTotalWidthOfFirstRow() {
      this.totalCalculatedFirstRowWidth = [...document.querySelector("table tr").children].reduce((tot, el) => tot + el.clientWidth, 0)
    },
    findPageSize() {
      const walk = vm => {
        if (vm.page_size) return vm.page_size
        if (vm.$parent) return walk(vm.$parent)
        return null
      }

      return walk(this)
    },
  },
  computed: {
    sizesStorageKey() {
      return SIZES_STORAGE_KEY + this.columnConfigurationId
    },
    minimumColumnSizes() {
      return this.$parent.$parent.$parent.$parent.minimumColumnSizes ||
        this.$parent.$parent.$parent.minimumColumnSizes ||
        this.$parent.$parent.minimumColumnSizes ||
        this.$parent.minimumColumnSizes
    },
    defaultColumnSizes() {
      return this.$parent.$parent.$parent.$parent.defaultColumnSizes ||
        this.$parent.$parent.$parent.defaultColumnSizes ||
        this.$parent.$parent.defaultColumnSizes ||
        this.$parent.defaultColumnSizes
    },
    computedColumnSizes() {
      if (this.disableLoadingContent) return null
      
      const minimum = this.minimumColumnSizes || {}
      const sizes = {...(this.columnSizes || {})}
      const defaultColumnSizes = this.defaultColumnSizes || {}

      this.columnVariables.forEach(fieldName => {
        const key = fieldName
        
        if (
          (minimum[key] && !sizes[key]) ||
          (sizes[key] < minimum[key])
        ) {
          sizes[key] = minimum[key]
        } else if (defaultColumnSizes[key] && !sizes[key]) {
          sizes[key] = defaultColumnSizes[key]
        }
      })

      const lastColumn = this.columnVariables[this.columnVariables.length - 1]
      if (lastColumn && !this.isTheadBiggerThanRows) {
        sizes[lastColumn] = 300
      }

      return Object.keys(sizes).length ? {...sizes} : null
    },
    
    numberOfColumnsThatDontHaveManualColumnSize() {
      return this.columnVariables.length - Object.keys(this.computedColumnSizes || {}).length
    },
    isLoading() {
      return this.skeletonClass === "ske"
    },
    numberOfItems() {
      const data = this.data
      return (data && data.length) ? data.length : 0
    },
    
    calculatedSizeFromManualColumnSize() {
      const sizes = Object.values(this.computedColumnSizes || {})
      return sizes.reduce((tot, curr) => tot + curr, 0)
    },
    width() {
      if (this.isTheadBiggerThanRows && this.theadWidth) return this.theadWidth + "px"
      if (this.isTheadBiggerThanRows) {
        if (this.theadWidth) return this.theadWidth + "px"
        else "100%"
      }

      const columnSizes = this.calculatedSizeFromManualColumnSize
      const width = this.totalCalculatedFirstRowWidth

      if (!width) return "auto"
      if (columnSizes < width) return `${width}px`

      return columnSizes !== "0px" ? `${columnSizes}px` : "auto"
    },
    skeletonArray() {
      const pageSize = this.findPageSize()
      const vars = this.columnVariables
      const ske1 = "asht asht asht asht asht"
      const ske2 = "asht asht asht"
      const arr = []

      for (let i = 0;i < pageSize;i++) {
        arr.push(
          vars.reduce((obj, key) => ({
            ...obj,
            [key]: (i % 2 === 0) ? ske1 : ske2
          }), {})
        )
      }

      return arr
    },
    computedData() {
      if (this.removeSkeletonArray) return this.data
      return this.isLoading ? this.skeletonArray : this.data
    },
    overflowingTableSpace() {
      return !this.isTheadBiggerThanRows
    },
    isTheadBiggerThanRows() {
      return this.totalCalculatedFirstRowWidth < this.theadWidth
    },
  },
  watch: {
    columnSizes(sizes) {
      localStorage.setItem(this.sizesStorageKey, JSON.stringify(sizes))
    },
    
    computedColumnSizes() {
      this.calculateWidthFromTheadElements()
    },
    columnVariables() {
      this.columnSizes = {}

      this.resetDimensionsAndReCalculateWidth()
      this.calculateWidthFromTheadElements()
    },
    isLoading() {
      this.columnSizes = {}
      this.calculateWidthFromTheadElements()
    },
  },
}

</script>

<style>

.delete-icon {
  transition-duration: .15s;
  padding: 0 12px;
}

.table .delete-icon:hover {
  color: #dc3545;
}

.overflowingTableSpace .table-td-cell, .overflowingTableSpace .table-cell {
  flex-basis: 200px !important;
  max-width: 200px !important;
  text-overflow: ellipsis;
  flex-shrink: 0;
  flex-grow: 0;
}

.Table .FormCheckbox {
  display: inline-block;
  transform: translateY(-1px);
}

.Table .btn-primary, .Table .btn-secondary, .Table .btn-danger, .Table .btn-success {
    border: none !important;
    padding: 3px 6px !important;
}

</style>

<style scoped>

.capitalize {
  text-transform: capitalize !important;
}

.table-cell, .table-td-cell {
  flex-basis: 100%;
}

.selected + .selected {
  border-top: none;
}

div {
  white-space: nowrap;
}

.empty-state-wrapper {
  flex-grow: 1;
  min-height: 400px;
}

.table {
  color: rgb(37,44,59);
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  position: relative;
}

tbody.hasHeightLimit {
  overflow: auto;
}

.overflowingTableSpace {
  overflow-x: auto;
  overflow-y: initial;
  padding-bottom: 50px;
}

.table-cell {
  flex-basis: 100%;
}

.final-item {
  position: absolute;
  padding-right: 0 !important;
  width: auto !important;
  border: none;
  padding: 0;
  right: 0 !important;
  right: -5px !important;
  height: 100%;
}

.final-item-wrapper {
  position: relative;
}

</style>
